import {
  Button,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Upload,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';
import { AppDispatch, RootState } from '../../../store/store';

import type { TablePaginationConfig, TableProps, TabsProps } from 'antd';
import { displayDateAndTime } from '../../../utils/datetime.util';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { getSmeManualPaymentThunk } from '../../../services/smeManaulPayment.service.ts/smeManualPaymentThunk';
import AccountEnquiryModal from '../../AccountEnquiry/AccountEnquiryModal';
import {
  SmeManualPaymentData,
  SmeManualPaymentUploadData,
} from '../../../features/smeManualPayment/smeManualPaymentSlice';
import {
  downloadManualPaymentTemplateThunk,
  downloadReportThunk,
} from '../../../services/reportService/reportThunk';
import Dragger from 'antd/es/upload/Dragger';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { InboxOutlined } from '@ant-design/icons';
import { FileTypeEnum } from '../../../enum/fileType';
import { uploadSmeManualPaymentFileThunk } from '../../../services/paymentService/paymentThunk';
import { numberWithCommas } from '../../../helpers/number';
import { handleDownload } from '../../../utils/preview.utils';
import moment from 'moment/moment';

const { Search } = Input;

const SmeManualPaymentPage = () => {
  const [manualPaymentResultModalVisible, setManualPaymentResultModalVisible] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');
  const { smeManualPayment } = useSelector((state: RootState) => state.smeManualPayment);
  const [searchFiled, setSearchFiled] = useState('agreementNo');
  const [downloading, setDownloading] = useState(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [smeManualPaymentResult, setSmeManualPaymentResult] = useState([]);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: { loanType: 'SME' },
    }),
    []
  );
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchState, setSearchState] = useState(initialParams.search);

  const tablePagination = {
    total: smeManualPayment?.data?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: smeManualPayment?.data?.currentPage,
    pageSize: smeManualPayment?.data?.pageSize,
  };

  const fetchSmeManualPayment = async (params?: any) => {
    await dispatch(getSmeManualPaymentThunk({ params }));
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    fetchSmeManualPayment(params);
  });

  const handleTableChange: TableProps<SmeManualPaymentData>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      sorter?.order == null
        ? {
            ...initialParams,
            ...filterState,
            pagination,
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          };

    fetchSmeManualPayment(param);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  const onSearch = (value: string) => {
    value = value.trim();
    fetchSmeManualPayment({
      ...initialParams,
      filters: filterState,
      search: { [searchFiled]: value },
    });
    setSearchState({ [searchFiled]: value });
  };

  const columns: ColumnsType<SmeManualPaymentData> = [
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      render: (text, record) => (
        <AccountEnquiryModal agreementNo={record?.accountDetails?.agreementNo ?? ''} />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text: number) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Pay Date',
      dataIndex: 'paymentDate',
      render: (text, record) => displayDateAndTime(record.paymentDate),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
    },
    {
      title: 'Reference No',
      dataIndex: 'referenceNo',
      key: 'referenceNo',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text, record) => displayDateAndTime(record.createdAt),
    },
  ];

  const smeManualPaymentResultColumn: ColumnsType<SmeManualPaymentUploadData> = [
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
    },
    {
      title: 'Reference No',
      dataIndex: 'referenceNo',
      key: 'referenceNo',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Error Message',
      dataIndex: 'error',
      key: 'error',
    },
  ];

  const onDownloadReport = async () => {
    setDownloading(true);
    setIsLoadingExcel(true);
    await dispatch(downloadManualPaymentTemplateThunk())
      .unwrap()
      .then((res) => {
        downloadFile(res, `SmeManualPaymentTemplate`, 'xlsx');
      })
      .catch((error) => {
        console.log(error);
        message.error('Failed to download template. Please try again later.');
      })
      .finally(() => setDownloading(false));
  };

  const downloadFile = (csvContent: any, fileName: any, filetype: any) => {
    const blob = new Blob([csvContent], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
    });
    const _win = window.navigator as any;
    if (_win && _win.msSaveBlob) {
      // For IE browser
      _win.msSaveBlob(blob, fileName);
      setIsLoadingExcel(false);
    } else {
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      setIsLoadingExcel(false);
    }
  };

  const handleOk = async () => {
    setUploading(true);
    setOpen(false);
    if (fileList.length > 2) {
      return message.error('Limit 1 files only');
    } else if (fileList.length == 0) {
      return message.error('Please upload file');
    } else {
      await form
        .validateFields()
        .then(async (values) => {
          const username = currentUser?.data?.username;

          const formData = new FormData();
          formData.append('fileType', FileTypeEnum.MANUAL_PAYMENT);
          formData.append('username', username ?? '');
          formData.append('file', fileList[0] as RcFile);

          dispatch(uploadSmeManualPaymentFileThunk(formData))
            .unwrap()
            .then((res) => {
              message.success('Submit Successfully');

              const url = window.URL.createObjectURL(new Blob([res]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute(
                'download',
                `${moment().format('YYYY_MM_DD')}_SME_Manual_Payment_Results.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              setTimeout(function () {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              message.success('Successfully download report.');

              handleCancel();
              setUploading(false);
              setFileList([]);
            })
            .catch(() => {
              setUploading(false);
              setFileList([]);
              handleCancel();
            });
        })
        .catch(() => {
          message.error('Something went wrong. Please try again later.');
          setFileList([]);
          setUploading(false);
          handleCancel();
        });
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const uploadProps: UploadProps = {
    maxCount: 1,
    fileList,
    beforeUpload: (file: RcFile) => {
      if (fileList.length > 0) {
        message.error('Only allowed 1 file');
        return Upload.LIST_IGNORE;
      }
      const correctFileType =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      if (!correctFileType) {
        message.error('Only allowed xlsx');
        return Upload.LIST_IGNORE;
      }

      fileList.push(file);
      return false;
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  return (
    <>
      <PageHeader className="px-0 mb-2" title="SME Manual Payment Listing" />
      <Row justify="start" className="mb-2" align="bottom">
        <Space>
          <Input.Group compact>
            <Select
              defaultValue="agreementNo"
              style={{ width: 200 }}
              onChange={(value) => setSearchFiled(value)}
              options={[
                { value: 'agreementNo', label: 'Agreement No' },
                { value: 'channel', label: 'Channel' },
              ]}
            />
            <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
          </Input.Group>{' '}
          <Button type="primary" onClick={() => onDownloadReport()} loading={isLoadingExcel}>
            Download File
          </Button>
          <Button type="primary" onClick={() => setOpen(true)} loading={uploading}>
            Upload File
          </Button>
        </Space>
      </Row>

      <Modal open={open} title="Upload File" onOk={handleOk} onCancel={handleCancel} width={600}>
        <Form form={form} name="uploadManualPaymentFile" layout="vertical" autoComplete="off">
          <Form.Item label="SME Manual Payment File">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Payment Result"
        open={manualPaymentResultModalVisible}
        onOk={() => setManualPaymentResultModalVisible(false)}
        onCancel={() => setManualPaymentResultModalVisible(false)}
      >
        <Table dataSource={smeManualPaymentResult} columns={smeManualPaymentResultColumn} />
      </Modal>
    </>
  );
};

export default SmeManualPaymentPage;
